import React, {useEffect, useState} from 'react';
import {motion} from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiCheckCircle, FiMail, FiCalendar, FiArrowLeft, FiGift} = FiIcons;

const ThankYou = () => {
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const data = localStorage.getItem('signupData');
    if (data) {
      setUserData(JSON.parse(data));
    }
  }, []);

  const nextSteps = [
    {
      icon: FiMail,
      title: "Check Your Email",
      description: "We've sent your login credentials and getting started guide to your inbox.",
      time: "Within 2 minutes"
    },
    {
      icon: FiCalendar,
      title: "Join Our Welcome Call",
      description: "Attend our next group call this week to meet the community and get personalized advice.",
      time: "This Wednesday 2PM EST"
    },
    {
      icon: FiGift,
      title: "Access Your Bonus",
      description: "Download your exclusive 'Business Growth Accelerator' blueprint from your member dashboard.",
      time: "Available now"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.8}}
          className="text-center mb-12"
        >
          <div className="bg-gradient-to-r from-green-500 to-blue-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheckCircle} className="text-white text-4xl" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Welcome to the Circle of Goodness<sup className="text-sm">TM</sup>!
          </h1>
          
          {userData && (
            <p className="text-xl text-gray-600 mb-2">
              Thanks {userData.firstName}! Your 7-day free trial has started.
            </p>
          )}
          
          <p className="text-lg text-gray-500">
            You're now part of an exclusive community of successful business owners.
          </p>
        </motion.div>

        {/* Success message */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.8, delay: 0.2}}
          className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">🎉 Your Trial is Active!</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">7 Days</div>
              <div className="text-sm text-gray-600">Full Access</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">$0</div>
              <div className="text-sm text-gray-600">No Charge</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">$2,497</div>
              <div className="text-sm text-gray-600">Total Value</div>
            </div>
          </div>
        </motion.div>

        {/* Next steps */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.8, delay: 0.4}}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">What Happens Next?</h2>
          <div className="space-y-6">
            {nextSteps.map((step, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 flex items-start space-x-4">
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={step.icon} className="text-white text-xl" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 mb-2">{step.description}</p>
                  <span className="text-sm text-blue-600 font-medium">{step.time}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Quick access */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.8, delay: 0.6}}
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-2xl shadow-2xl text-center mb-8"
        >
          <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
          <p className="text-lg mb-6 opacity-90">
            Access your member dashboard and start your business transformation today.
          </p>
          <button className="bg-white text-blue-600 font-bold px-8 py-4 rounded-full hover:bg-gray-100 transition-colors duration-300 shadow-lg">
            Access Your Dashboard
          </button>
        </motion.div>

        {/* Support info */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.8, delay: 0.8}}
          className="bg-gray-50 p-6 rounded-xl text-center"
        >
          <h4 className="font-bold text-gray-900 mb-2">Need Help Getting Started?</h4>
          <p className="text-gray-600 mb-4">
            Our support team is here to help you succeed. Reach out anytime!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              Contact Support
            </button>
            <button
              onClick={() => navigate('/')}
              className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center"
            >
              <SafeIcon icon={FiArrowLeft} className="mr-2" />
              Back to Home
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ThankYou;